import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

const VitalityDuplicatedTable = ({
  data,
  columns,
  pagination = true,
  onSubmit,
  statusLabel,
  pageSize,
  onFieldChange,
  hideColumnForRow,
  errors = {},
}) => {
  const PAGE_SIZE = pageSize || 10;

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  const totalPages = Math.ceil(data.length / PAGE_SIZE);

  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const currentData = pagination
    ? data.slice(startIndex, startIndex + PAGE_SIZE)
    : data;

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const getPaginationPages = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 5) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }

    if (currentPage >= totalPages - 4) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    // Current page in the middle
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  const paginationPages = getPaginationPages();

  return (
    <>
      <div className="vh-patient-table-wrapper">
        <table className="vh-patient-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.dataField}
                  className={`
                    ${column.dataField === "email" ? "vh-patient-table-email-column" : ""}`}
                >
                  {column.text}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {currentData.length > 0 ? (
              currentData.map((row) => (
                <tr key={row.id}>
                  {columns.map((column) => {
                    if (hideColumnForRow?.(column, row)) {
                      return (
                        <td key={column.dataField} className="vh-patient-table-empty-cell" />
                      );
                    }

                    return (
                      <td
                        key={column.dataField}
                        className={`
                      ${column.dataField === "email" ? "vh-patient-table-email-column" : ""}`}
                      >
                        {column.render ? (
                          column.render(
                            row[column.dataField],
                            row,
                            onFieldChange,
                            errors[`${row.id}_${column.dataField}`],
                          )
                        ) : column.dataField === "status" ? (
                          <button
                            type="button"
                            className="vh-table-action-btn"
                            onClick={() => onSubmit?.(row)}
                          >
                            {statusLabel}
                          </button>
                        ) : (
                          row[column.dataField] || "-"
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="vh-table-no-data">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pagination && totalPages > 1 && (
        <div className="vh-table-pagination">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="vh-pagination-prev"
          >
            <span className="vh-pagination-chevron">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M15.2928 2.29291L16.707 3.70712L8.41414 12L16.707 20.2929L15.2928 21.7071L5.58571 12L15.2928 2.29291Z"
                  fill="#C2C2CA"
                />
              </svg>
            </span>
            <span>Previous</span>
          </button>

          <div className="vh-pagination-pages">
            {paginationPages.map((page, index) => {
              if (page === "...") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="vh-pagination-ellipsis"
                  >
                    ...
                  </span>
                );
              }

              return (
                <button
                  key={page}
                  type="button"
                  className={`vh-pagination-page ${
                    currentPage === page ? "active" : ""
                  }`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="vh-pagination-next"
          >
            <span>Next</span>
            <span className="vh-pagination-chevron">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ transform: "rotate(180deg)" }}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M15.2928 2.29291L16.707 3.70712L8.41414 12L16.707 20.2929L15.2928 21.7071L5.58571 12L15.2928 2.29291Z"
                  fill="#C2C2CA"
                />
              </svg>
            </span>
          </button>
        </div>
      )}
    </>
  );
};

VitalityDuplicatedTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  hideColumnForRow: PropTypes.func,
  pagination: PropTypes.bool,
  onSubmit: PropTypes.func,
  errors: PropTypes.object,
};

export default VitalityDuplicatedTable;
